import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Chip, Divider, useTheme, Grid, SvgIcon } from '@mui/material';
import {
  Group as GroupIcon,
  Visibility as VisibilityIcon,
  AttachMoney as MoneyIcon,
  ThumbUp as ThumbUpIcon,
  Comment as CommentIcon,
  Share as ShareIcon,

  SentimentSatisfiedAlt as PositiveIcon,
  SentimentNeutral as NeutralIcon,
  SentimentDissatisfied as NegativeIcon,
  AutoAwesome as AIIcon,
  Speed as ImpactIcon,
  PeopleAlt as AudienceIcon,
  Category as CategoryIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Map as MapIcon,
  Straighten as StraightenIcon,
  Star as DedicatedIcon,
  Groups as SharedIcon,
  FormatQuote as MentionIcon,
  Remove as NoneExposureIcon,
  Public as PublicIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  TitleOutlined as HighlightTitleIcon,
  Key as KeyMessageIcon
} from '@mui/icons-material';
import { formatNumber } from '../../../utils/contentProcessor';
import { colors } from '../../../design-system/tokens';
import Tooltip from '../../atoms/Tooltip/Tooltip';
import { useBrand } from '../../../context/BrandContext';
import useBriefingKeywords from '../../../hooks/useBriefingKeywords';
import { getEffectiveTier } from '../../../utils/effectiveTier';
import { formatDuration, getBrandDurationSeconds, getTotalDurationSeconds, isTimeBasedMedia } from '../../../utils/editorialOccupancy';

// Custom Tier Icon (Segmented Pyramid)
const TierIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M12 3L9.5 8h5z M8.5 10L6 15h12l-2.5-5z M5 17L2.5 22h19L19 17z" />
  </SvgIcon>
);

const FALLBACK_BRAND_NAME = 'a marca';
const MIA_ACCENT = '#6fa84f';
const MIA_COMPETITOR = '#56636d';
const MIA_SURFACE = '#121920';
const MIA_SURFACE_RAISED = '#1d2630';
const MIA_TEXT = '#edf3f7';
const MIA_MUTED = '#9aa7b1';

const namesMatch = (a, b) => {
  const nameA = String(a || '').toLowerCase().trim();
  const nameB = String(b || '').toLowerCase().trim();
  return !!(nameA && nameB && (nameA === nameB || nameA.includes(nameB) || nameB.includes(nameA)));
};

const getArticleTagNames = (article) => (article?.tags || [])
  .map(tag => (typeof tag === 'object' ? tag?.name : null))
  .filter(Boolean);

const TooltipContent = ({ eyebrow, title, description, insight, evidence, inverted = false }) => (
  <Box sx={{ maxWidth: 320, color: inverted ? 'rgba(17, 24, 39, 0.92)' : 'inherit' }}>
    {eyebrow && (
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 0.5,
          fontSize: '0.65rem',
          fontWeight: 700,
          letterSpacing: 1,
          textTransform: 'uppercase',
          color: inverted ? 'rgba(107, 114, 128, 0.95)' : 'rgba(222, 230, 238, 0.72)'
        }}
      >
        {eyebrow}
      </Typography>
    )}
    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, lineHeight: 1.35, color: inverted ? 'rgba(17, 24, 39, 0.96)' : MIA_TEXT }}>
      {title}
    </Typography>
    {description && (
      <Typography sx={{ mt: 0.75, fontSize: '0.76rem', lineHeight: 1.55, color: inverted ? 'rgba(55, 65, 81, 0.88)' : MIA_MUTED }}>
        {description}
      </Typography>
    )}
    {evidence && (
      <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: inverted ? 'rgba(15, 23, 42, 0.12)' : 'divider' }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: inverted ? 'rgba(17, 24, 39, 0.96)' : MIA_TEXT, mb: 0.35 }}>
          Base da classificação
        </Typography>
        <Typography sx={{ fontSize: '0.74rem', lineHeight: 1.55, color: inverted ? 'rgba(55, 65, 81, 0.88)' : MIA_MUTED }}>
          {evidence}
        </Typography>
      </Box>
    )}
    {insight && (
      <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: inverted ? 'rgba(15, 23, 42, 0.12)' : 'divider' }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: inverted ? 'rgba(17, 24, 39, 0.96)' : MIA_TEXT, mb: 0.35 }}>
          Insight da IA
        </Typography>
        {React.isValidElement(insight) ? insight : (
          <Typography sx={{ fontSize: '0.74rem', lineHeight: 1.55, color: inverted ? 'rgba(55, 65, 81, 0.88)' : MIA_MUTED }}>
            {insight}
          </Typography>
        )}
      </Box>
    )}
  </Box>
);

const getExposureConfig = (exposure, brandName = FALLBACK_BRAND_NAME) => {
  switch (exposure) {
    case 'dedicated':
      return {
        icon: DedicatedIcon,
        label: 'Dedicada',
        description: `Matéria focada em ${brandName}`,
        color: colors.exposure?.dedicated?.main || '#4caf50'
      };
    case 'shared':
      return {
        icon: SharedIcon,
        label: 'Compartilhada',
        description: `${brandName} aparece junto com concorrentes ou outras marcas`,
        color: colors.exposure?.shared?.main || '#2196f3'
      };
    case 'mention':
      return {
        icon: MentionIcon,
        label: 'Citação',
        description: `${brandName} aparece apenas como uma menção breve`,
        color: colors.exposure?.mention?.main || '#9e9e9e'
      };
    case 'highlight':
      return {
        icon: HighlightTitleIcon,
        label: 'Highlight',
        description: `${brandName} aparece em destaque (geralmente no título)`,
        color: '#f59e0b'
      };
    case 'none':
      return {
        icon: NoneExposureIcon,
        label: 'Sem exposição',
        description: `${brandName} não é mencionada diretamente`,
        color: colors.exposure?.none?.main || '#9e9e9e'
      };
    default:
      // Fallback para evitar valores vazios
      return {
        icon: MentionIcon,
        label: 'Mencionada',
        description: 'Presença identificada no conteúdo.',
        color: '#9e9e9e'
      };
  }
};

const getSentimentConfig = (sentiment) => {
  switch (sentiment) {
    case 'positive':
      return {
        icon: PositiveIcon,
        label: 'Positivo',
        color: colors.sentiment.positive.main
      };
    case 'negative':
      return {
        icon: NegativeIcon,
        label: 'Negativo',
        color: colors.sentiment.negative.main
      };
    default:
      return {
        icon: NeutralIcon,
        label: 'Neutro',
        color: colors.sentiment.neutral.main
      };
  }
};

const formatCategory = (category) => {
  if (!category) return null;

  const categoryMap = {
    portal: 'Portal',
    blog: 'Blog',
    site: 'Site',
    jornal: 'Jornal',
    revista: 'Revista',
    'rede-social': 'Rede Social',
    governo: 'Governo',
    outro: 'Outro'
  };

  return categoryMap[category] || category;
};

const getSourceReach = (article) => {
  const value = [
    article?.domainInfo?.abrangencia,
    article?.domainInfo?.scope,
    article?.profileInfo?.abrangencia,
    article?.profile?.abrangencia,
    article?.abrangencia,
    article?.coverage,
    article?.scope,
    article?.region,
    article?.reach
  ].find(item => typeof item === 'string' && item.trim());

  return value?.trim() || null;
};

const EntityBreakdownChart = ({ entities = [], inverted = false }) => {
  const visibleEntities = entities
    .filter((entity) => entity && entity.name)
    .slice(0, 5);

  if (visibleEntities.length === 0) return null;

  return (
    <Box sx={{ mt: 0.25 }}>
      {visibleEntities.map((entity) => {
        const percent = Math.round((entity.participationPercent || 0) * 100);
        const isBrand = entity.type === 'brand';
        const markerColor = isBrand ? MIA_ACCENT : MIA_COMPETITOR;
        const markerLabel = isBrand ? 'M' : 'C';

        const expConfig = entity.exposure ? getExposureConfig(entity.exposure, entity.name) : null;
        const sentConfig = entity.sentiment ? getSentimentConfig(entity.sentiment) : null;
        const ExpIcon = expConfig?.icon;
        const SentIcon = sentConfig?.icon;

        return (
          <Box key={`${entity.type}-${entity.name}`} sx={{ mb: 1.2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flexWrap: 'wrap' }}>
                <Box
                  component="span"
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: markerColor,
                    color: isBrand ? '#121922' : 'rgba(255,255,255,0.86)',
                    boxShadow: isBrand ? `0 0 10px ${markerColor}40` : 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: '0 0 auto',
                    fontSize: '0.58rem',
                    fontWeight: 800,
                    lineHeight: 1
                  }}
                  title={isBrand ? 'Marca monitorada' : 'Concorrente'}
                >
                  {markerLabel}
                </Box>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: inverted ? 'rgba(17, 24, 39, 0.96)' : MIA_TEXT, whiteSpace: 'nowrap' }}>
                  {entity.name}
                </Typography>

                {/* Chips de Sentimento e Exposição */}
                {expConfig && ExpIcon && (
                  <Chip
                    icon={<ExpIcon sx={{ fontSize: 11, color: '#fff !important' }} />}
                    label={expConfig.label}
                    size="small"
                    sx={{
                      height: 16,
                      color: '#fff',
                      backgroundColor: expConfig.color,
                      opacity: percent === 0 ? 0.5 : 0.85,
                      '& .MuiChip-label': { px: 0.5, fontSize: '0.55rem', fontWeight: 600, letterSpacing: 0.2 },
                      '& .MuiChip-icon': { ml: 0.5 }
                    }}
                  />
                )}
                {sentConfig && SentIcon && (
                  <Chip
                    icon={<SentIcon sx={{ fontSize: 11, color: '#fff !important' }} />}
                    label={sentConfig.label}
                    size="small"
                    sx={{
                      height: 16,
                      color: '#fff',
                      backgroundColor: sentConfig.color,
                      opacity: percent === 0 ? 0.5 : 0.85,
                      '& .MuiChip-label': { px: 0.5, fontSize: '0.55rem', fontWeight: 600, letterSpacing: 0.2 },
                      '& .MuiChip-icon': { ml: 0.5 }
                    }}
                  />
                )}
              </Box>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: inverted ? 'rgba(55, 65, 81, 0.88)' : MIA_MUTED, flexShrink: 0 }}>
                {percent}% · {(entity.areaCm2 || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} cm²
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                height: 6,
                borderRadius: 999,
                bgcolor: inverted ? 'rgba(15, 23, 42, 0.10)' : 'rgba(149, 163, 173, 0.14)',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  width: `${Math.max(percent, entity.areaCm2 > 0 ? 2 : 0)}%`,
                  height: '100%',
                  borderRadius: 999,
                  bgcolor: markerColor
                }}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const ModalArticleInfoBrandAware = ({ article, sx = {}, enableBriefingKeywords = true, section = null, hideQualitativeButton = false }) => {
  const theme = useTheme();
  const { activeBrandData } = useBrand();
  const { keywords: briefingKeywords } = useBriefingKeywords({ enabled: enableBriefingKeywords });
  const isDark = theme.palette.mode === 'dark';
  const infoText = isDark ? 'rgba(238,242,247,0.88)' : '#111827';
  const infoValue = isDark ? 'rgba(238,242,247,0.72)' : 'rgba(17,24,39,0.76)';
  const infoMuted = isDark ? 'rgba(238,242,247,0.56)' : '#6b7280';
  const infoHeading = isDark ? 'rgba(238,242,247,0.70)' : 'rgba(17,24,39,0.72)';
  const infoLine = isDark ? 'rgba(255,255,255,0.075)' : 'rgba(15,23,42,0.08)';
  const activeBrandName = activeBrandData?.name?.trim() || FALLBACK_BRAND_NAME;
  const effectiveTier = getEffectiveTier(article);
  const isSocialMedia = ['Twitter', 'Instagram', 'Facebook', 'YouTube', 'TikTok', 'LinkedIn', 'Pinterest', 'Threads', 'Kwai'].includes(article.mediaType);
  const isOnline = article.mediaType === 'Online';
  const isPrintMedia = ['Jornal', 'Revista'].includes(article.mediaType);
  const isTimeMedia = isTimeBasedMedia(article);
  const totalDurationSeconds = getTotalDurationSeconds(article);
  const brandDurationSeconds = getBrandDurationSeconds(article);
  const broadcastAudience = article.audience || article.audienceMonthly || article.subscribersNumber || article.viewsNumber || 0;
  const hasSourceData = Boolean(
    article.domainInfo?.tipo ||
    article.domainInfo?.category ||
    article.domainInfo?.segmento ||
    article.domainInfo?.abrangencia ||
    article.domainInfo?.estado ||
    article.domainInfo?.cidade ||
    article.domainInfo?.sede
  );
  const sourceReach = getSourceReach(article);
  const sourceType = article.domainInfo?.tipo || formatCategory(article.domainInfo?.category) || formatCategory(article.mediaType);
  const strategicSegment = article.sourceCategory || article.sourceCategories?.[0];
  const sourceSegment = article.tierSource === 'category'
    ? strategicSegment || article.domainInfo?.segmento
    : article.domainInfo?.segmento || strategicSegment;
  const sourceLocation = [
    article.domainInfo?.cidade,
    article.domainInfo?.uf || article.domainInfo?.estado || article.domainInfo?.sede
  ].filter(Boolean).join(' / ');
  const occupancy = article.editorialOccupancy || article.onlineCentimetragem || {};
  const articleTagNames = getArticleTagNames(article);
  const storedEntityBreakdown = Array.isArray(occupancy.entityBreakdown)
    ? occupancy.entityBreakdown.filter(entity =>
      entity?.name && ((entity.participationPercent || 0) > 0 || (entity.areaCm2 || 0) > 0 || (entity.durationSeconds || 0) > 0)
    )
    : [];
  const totalEditorialAreaForFallback =
    occupancy.editorialAreaTotalCm2 ||
    occupancy.valueCm2 ||
    occupancy.editorialAreaBrandRelevantCm2 ||
    0;
  const briefingCompetitorNames = [
    ...new Set((briefingKeywords?.competitor || []).map(item => item?.competitor).filter(Boolean))
  ];
  const missingCompetitorNames = articleTagNames.filter(tagName => {
    const isActiveBrand = namesMatch(tagName, activeBrandName);
    const alreadyDisplayed = storedEntityBreakdown.some(entity => namesMatch(entity.name, tagName));
    const isBriefingCompetitor = briefingCompetitorNames.some(competitorName => namesMatch(competitorName, tagName));
    return !isActiveBrand && !alreadyDisplayed && isBriefingCompetitor;
  });
  const fallbackCompetitorShare = missingCompetitorNames.length > 0
    ? Math.min(0.1, 0.24 / missingCompetitorNames.length)
    : 0;
  const fallbackCompetitors = missingCompetitorNames.map(name => ({
    name,
    type: 'competitor',
    areaCm2: totalEditorialAreaForFallback * fallbackCompetitorShare,
    participationPercent: fallbackCompetitorShare,
    exposure: 'mention',
    sentiment: 'neutral',
    sentimentSummary: 'Detectado pela classificação do artigo.'
  }));
  const entityBreakdown = (() => {
    const combined = [...storedEntityBreakdown, ...fallbackCompetitors];
    const totalParticipation = combined.reduce((sum, entity) => sum + (entity.participationPercent || 0), 0);
    if (totalParticipation <= 1) return combined;

    return combined.map(entity => ({
      ...entity,
      participationPercent: (entity.participationPercent || 0) / totalParticipation,
      areaCm2: (entity.areaCm2 || 0) / totalParticipation
    }));
  })();
  const entityBreakdownInsight = entityBreakdown.length > 0
    ? <EntityBreakdownChart entities={entityBreakdown} />
    : null;

  const sentimentConfig = article.sentiment ? getSentimentConfig(article.sentiment) : null;
  const SentimentIconComponent = sentimentConfig?.icon;
  const exposureConfig = article.exposure ? getExposureConfig(article.exposure, activeBrandName) : null;
  const ExposIcon = exposureConfig?.icon;
  const sentimentSummary = article.sentimentSummary || `Análise de sentimento: ${sentimentConfig?.label || 'Neutro'}`;
  const exposureSummary = article.exposureAnalysis?.summary;
  const competitorAnalysis = article.exposureAnalysis?.competitorAnalysis || [];
  const tagNames = articleTagNames;
  const classificationName = tagNames[0] || activeBrandName;
  // Entidade dominante = quem tem mais espaço real no texto (ignorando quem tem 0%)
  const activeEntities = entityBreakdown.filter(e => (e.participationPercent || 0) > 0 || (e.areaCm2 || 0) > 0);
  const dominantEntity = activeEntities.length > 0
    ? activeEntities.reduce((prev, curr) => {
      const prevWeight = prev.participationPercent || prev.areaCm2 || 0;
      const currWeight = curr.participationPercent || curr.areaCm2 || 0;
      return currWeight > prevWeight ? curr : prev;
    })
    : null;
  const dominantEntityName = dominantEntity?.name || classificationName;
  const brandEntity = activeEntities.find(entity => entity.type === 'brand') || entityBreakdown.find(entity => entity.type === 'brand');
  const measuredEntity = brandEntity && ((brandEntity.areaCm2 || 0) > 0 || (brandEntity.participationPercent || 0) > 0)
    ? brandEntity
    : dominantEntity;
  const entityMeasuredAreaCm2 = activeEntities.reduce((sum, entity) => sum + (entity.areaCm2 || 0), 0);
  const totalEditorialCm2 = totalEditorialAreaForFallback || entityMeasuredAreaCm2;
  const occupancyCm2 = isPrintMedia
    ? (
      occupancy.editorialAreaBrandRelevantCm2 ||
      occupancy.valueCm2 ||
      (brandEntity?.areaCm2 || 0) ||
      (measuredEntity?.areaCm2 || 0) ||
      0
    )
    : (
      (brandEntity?.areaCm2 || 0) ||
      occupancy.editorialAreaBrandRelevantCm2 ||
      occupancy.valueCm2 ||
      (measuredEntity?.areaCm2 || 0) ||
      0
    );
  const spokespersonFound = [
    occupancy.spokespersonFound,
    brandEntity?.spokespersonFound,
    article.spokespersonFound
  ].find(value => value && value !== 'Identificado manualmente');
  const keyMessagesFound = Array.from(new Set([
    ...(Array.isArray(occupancy.keyMessagesFound) ? occupancy.keyMessagesFound : []),
    ...(Array.isArray(brandEntity?.keyMessagesFound) ? brandEntity.keyMessagesFound : []),
    ...(Array.isArray(article.keyMessagesFound) ? article.keyMessagesFound : []),
    ...entityBreakdown.flatMap(entity => Array.isArray(entity?.keyMessagesFound) ? entity.keyMessagesFound : [])
  ].filter(Boolean)));
  const competitorMentionAreaCm2 = activeEntities
    .filter(entity => entity.type === 'competitor' && (!entity.exposure || entity.exposure === 'mention'))
    .reduce((sum, entity) => sum + (entity.areaCm2 || 0), 0);
  const shouldAttributeContextToBrand = (
    (article.exposure === 'dedicated' || occupancy.exposureUsed === 'dedicated' || dominantEntity?.exposure === 'dedicated') &&
    dominantEntity?.type === 'brand' &&
    activeEntities.every(entity => entity.type === 'brand' || !entity.exposure || entity.exposure === 'mention')
  );
  const attributedOccupancyCm2 = shouldAttributeContextToBrand && totalEditorialCm2 > 0
    ? Math.max(0, totalEditorialCm2 - competitorMentionAreaCm2)
    : occupancyCm2;
  const attributedOccupancyPercent = totalEditorialCm2 > 0
    ? attributedOccupancyCm2 / totalEditorialCm2
    : (occupancy.brandParticipationPercent || 0);
  const hasEditorialMeasurement = Boolean(
    (isTimeMedia && (totalDurationSeconds > 0 || brandDurationSeconds > 0)) ||
    totalEditorialCm2 > 0 ||
    attributedOccupancyCm2 > 0 ||
    occupancyCm2 > 0 ||
    occupancy.enabled ||
    activeEntities.some(entity => (entity.areaCm2 || 0) > 0 || (entity.participationPercent || 0) > 0)
  );
  const evidenceText = tagNames.length > 0
    ? `Classificação do artigo: ${tagNames.join(', ')}`
    : `Classificação do artigo: ${classificationName}`;
  const rowBaseSx = {
    minHeight: 32,
    py: 0.78,
    borderBottom: '1px solid',
    borderColor: infoLine
  };
  const rowLabelSx = {
    color: `${infoText} !important`,
    fontSize: '0.79rem',
    fontWeight: 760,
    lineHeight: 1.2,
    minWidth: 0
  };
  const rowValueSx = {
    color: `${infoValue} !important`,
    fontSize: '0.79rem',
    fontWeight: 760,
    lineHeight: 1.2,
    textAlign: 'right',
    flexShrink: 0
  };
  const rowIconSx = {
    fontSize: 16,
    color: `${infoMuted} !important`,
    flex: '0 0 auto'
  };
  const chipBaseSx = {
    height: 24,
    borderRadius: 999,
    color: `${infoText} !important`,
    backgroundColor: isDark ? 'rgba(238,242,247,0.13)' : 'rgba(15,23,42,0.08)',
    border: '1px solid',
    borderColor: isDark ? 'rgba(238,242,247,0.08)' : 'rgba(15,23,42,0.08)',
    fontWeight: 760,
    '& .MuiChip-label': {
      px: 0.8,
      fontSize: '0.76rem',
      fontWeight: 760
    },
    '& .MuiChip-icon': {
      ml: 0.65,
      color: `${infoMuted} !important`
    }
  };

  const InfoItem = ({ icon: Icon, icons = [], label, value, tooltip, card = false, color }) => {
    if (!value && value !== 0) return null;
    const normalizedLabel = String(label || '').toLowerCase();
    const shouldUseCard = card || [
      'inscrit',
      'seguidor',
      'visualiza',
      'page views',
      'valora',
      'likes',
      'coment',
      'compartilh',
      'audi',
      'tiragem',
      'circula',
      'abrang'
    ].some((token) => normalizedLabel.includes(token));

    if (shouldUseCard) {
      // Define colors based on label like the public modal
      let iconColor = color || theme.palette.primary.main;
      if (!color) {
        if (normalizedLabel.includes('valora')) iconColor = '#22c55e';
        else if (normalizedLabel.includes('visualiza') || normalizedLabel.includes('page view')) iconColor = '#f59e0b';
        else if (normalizedLabel.includes('likes') || normalizedLabel.includes('curtida')) iconColor = '#ec4899';
        else if (normalizedLabel.includes('coment')) iconColor = '#8b5cf6';
        else if (normalizedLabel.includes('compartilha')) iconColor = '#06b6d4';
        else if (normalizedLabel.includes('abrang')) iconColor = '#14b8a6';
        else if (normalizedLabel.includes('mensal')) iconColor = '#a855f7';
        else if (normalizedLabel.includes('audiência') || normalizedLabel.includes('inscrit') || normalizedLabel.includes('seguidor')) iconColor = '#3b82f6';
      }

      const isFullWidth = normalizedLabel.includes('mensal');

      const content = (
        <Grid item xs={isFullWidth ? 12 : 6}>
          <Box sx={{ 
            px: 0.65,
            py: 0.35,
            bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'action.hover', 
            borderRadius: 1.5, 
            textAlign: 'center', 
            minHeight: 62,
            height: 62,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', gap: 0.5, mb: 0.2, justifyContent: 'center' }}>
              {icons && icons.length > 1 ? (
                icons.map((cfg, idx) => {
                  const Ico = cfg.icon;
                  return <Ico key={idx} sx={{ fontSize: 15, color: cfg.color || iconColor }} />;
                })
              ) : Icon ? (
                <Icon sx={{ fontSize: 15, color: iconColor }} />
              ) : null}
            </Box>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: iconColor, lineHeight: 1.02, mb: 0.08 }} translate="no">
              {value}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.58rem', color: infoMuted, lineHeight: 1.05 }}>
              {label}
            </Typography>
          </Box>
        </Grid>
      );

      if (!tooltip) return content;

      return (
        <Tooltip title={tooltip} arrow placement="top">
          {content}
        </Tooltip>
      );
    }

    const content = (
      <Box sx={{
        ...rowBaseSx,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        '&:last-of-type': { borderBottom: 0 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minWidth: 0 }}>
          <Icon sx={rowIconSx} />
          <Typography variant="body2" noWrap sx={rowLabelSx}>
            {label}
          </Typography>
        </Box>
        <Typography variant="body2" translate="no" noWrap sx={rowValueSx}>
          {value}
        </Typography>
      </Box>
    );

    if (!tooltip) return content;

    return (
      <Tooltip title={tooltip} arrow placement="top">
        {content}
      </Tooltip>
    );
  };

  const SectionTitle = ({ children }) => (
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 700,
        color: `${infoHeading} !important`,
        mb: 1.1,
        mt: 0.85,
        textTransform: 'uppercase',
        fontSize: '0.72rem',
        letterSpacing: 1
      }}
    >
      {children}
    </Typography>
  );

  const MetricGrid = ({ children }) => (
    <Grid container spacing={0.9} sx={{ mb: 1.15, mt: 0.35 }}>
      {children}
    </Grid>
  );

  const StatusRow = ({ icon: Icon = AIIcon, label, children }) => (
    <Box
      sx={{
        ...rowBaseSx,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        minHeight: 34
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minWidth: 0 }}>
        <Icon sx={rowIconSx} />
        <Typography noWrap variant="body2" sx={rowLabelSx}>
          {label}
        </Typography>
      </Box>
      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', maxWidth: '58%' }}>
        {children}
      </Box>
    </Box>
  );

  let monthlyAudience = 0;
  let dailyAudience = 0;
  let pageViews = 0;

  if (isOnline || isPrintMedia) {
    const audienceMonthly = article.audienceMonthly
      || article.monthlyAudience
      || article.audienceMonthlyEstimate
      || article.domainInfo?.estimatedAudience
      || article.domainInfo?.audienceMonthly
      || article.domainInfo?.monthlyVisits
      || 0;
    dailyAudience = article.audience || article.dailyAudience || article.audienceDaily || article.subscribersNumber || 0;
    monthlyAudience = audienceMonthly > 0
      ? audienceMonthly
      : Math.round(dailyAudience * (365 / 12));
    if (!dailyAudience && monthlyAudience > 0) {
      dailyAudience = Math.round(monthlyAudience / (365 / 12));
    }
    pageViews = article.pageViews || article.pageViewsMonthly || Math.round(monthlyAudience * 3);
  }

  const uniqueSentiments = Array.from(new Set(entityBreakdown.map(e => e.sentiment).filter(Boolean)));
  const uniqueExposures = Array.from(new Set(entityBreakdown.map(e => e.exposure).filter(Boolean)));
  const sentimentIcons = uniqueSentiments.map(s => getSentimentConfig(s)).filter(Boolean);
  const exposureIcons = uniqueExposures.map(e => getExposureConfig(e, dominantEntityName)).filter(Boolean);
  const canShowQualitativeAnalysis = entityBreakdown.length > 0 || !!article.exposure;
  const openQualitativeAnalysis = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('openKpiaModal', { detail: { article } }));
    }
  };

  const qualitativeAnalysisButton = canShowQualitativeAnalysis ? (
    <Box sx={{ mb: 1.1 }}>
      <Box
        component="button"
        type="button"
        onClick={openQualitativeAnalysis}
        sx={{
          width: '100%',
          minHeight: 40,
          border: 0,
          borderRadius: 1.5,
          bgcolor: '#4f8f38',
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          px: 1.35,
          py: 0.75,
          fontFamily: 'inherit',
          cursor: 'pointer',
          textAlign: 'center',
          transition: 'border-color 160ms ease, background-color 160ms ease, color 160ms ease',
          '&:hover': {
            bgcolor: '#447c30'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.85, minWidth: 0 }}>
          <AIIcon sx={{ fontSize: 17, color: '#ffffff', flex: '0 0 auto' }} />
          <Typography
            component="span"
            sx={{
              color: '#ffffff !important',
              fontWeight: 780,
              fontSize: '0.78rem',
              lineHeight: 1.1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            Ver Análise Qualitativa
          </Typography>
        </Box>
      </Box>
    </Box>
  ) : null;

  return (
    <Box
      sx={{
        my: 1,
        p: 0,
        background: 'transparent',
        borderRadius: 0,
        border: 0,
        boxShadow: 'none',
        position: 'relative',
        overflow: 'visible',
        '& > *': {
          position: 'relative',
          zIndex: 1
        },
        '& .MuiTypography-root': {
          color: infoMuted
        },
        '& strong': {
          color: infoText
        },
        '& .MuiDivider-root': {
          borderColor: infoLine
        },
        ...sx
      }}
    >

      {/* -- Seção: Análise Qualitativa + Performance + Fonte --------------- */}
      {(!section || section === 'fonte') && (
        <>
          {/* Só renderiza o botão aqui quando não há section definida e o prop de ocultar é falso */}
          {!section && !hideQualitativeButton && qualitativeAnalysisButton}

          <SectionTitle>Performance</SectionTitle>

      {isSocialMedia && (
        <MetricGrid>
          <InfoItem
            icon={GroupIcon}
            label={article.mediaType === 'YouTube' ? 'Inscritos' : 'Seguidores'}
            value={formatNumber(article.subscribersNumber || 0)}
          />
          <InfoItem icon={VisibilityIcon} label="Visualizações" value={formatNumber(article.viewsNumber || 0)} />
          <InfoItem icon={MoneyIcon} label="Valoração" value={`R$ ${(article.valuation || 0).toLocaleString('pt-BR')}`} />
          <InfoItem icon={ThumbUpIcon} label="Likes" value={formatNumber(article.likesNumber || 0)} />
          <InfoItem icon={CommentIcon} label="Comentários" value={formatNumber(article.commentsNumber || 0)} />
          {article.sharesNumber > 0 && (
            <InfoItem icon={ShareIcon} label="Compartilhamentos" value={formatNumber(article.sharesNumber)} />
          )}
        </MetricGrid>
      )}

      {isOnline && (
        <MetricGrid>
          <InfoItem
            icon={AudienceIcon}
            label="Audiência Mensal"
            value={formatNumber(monthlyAudience)}
          />
          <InfoItem icon={AudienceIcon} label="Audiência Diária" value={formatNumber(dailyAudience)} />
          <InfoItem
            icon={VisibilityIcon}
            label="Page Views"
            value={formatNumber(pageViews)}
          />
          <InfoItem
            icon={VisibilityIcon}
            label="Page Views Diário"
            value={formatNumber(Math.round(dailyAudience * 3))}
            tooltip="Page Views Diário = Audiência Diária x 3"
          />
          <InfoItem card icon={MoneyIcon} label="Valoração" value={`R$ ${(article.valuation || 0).toLocaleString('pt-BR')}`} />
        </MetricGrid>
      )}

      {isPrintMedia && (
        <MetricGrid>
          <InfoItem
            card
            icon={AudienceIcon}
            label="Tiragem / Circulação"
            value={formatNumber(monthlyAudience || dailyAudience)}
          />
          <InfoItem card icon={MoneyIcon} label="Valoração" value={`R$ ${(article.valuation || 0).toLocaleString('pt-BR')}`} />
        </MetricGrid>
      )}

      {isTimeMedia && (
        <MetricGrid>
          <InfoItem card icon={AudienceIcon} label="Audiência" value={formatNumber(broadcastAudience)} />
          <InfoItem card icon={MoneyIcon} label="Valoração" value={`R$ ${(article.valuation || 0).toLocaleString('pt-BR')}`} />
          <InfoItem
            card
            icon={ScheduleIcon}
            label="Minutagem"
            value={brandDurationSeconds > 0 ? formatDuration(brandDurationSeconds) : 'Pendente'}
            tooltip={totalDurationSeconds > 0
              ? `Minutagem da marca: ${formatDuration(brandDurationSeconds)} de ${formatDuration(totalDurationSeconds)} da duração total.`
              : 'Informe a duração total e a minutagem da marca.'}
          />
        </MetricGrid>
      )}

      <Divider sx={{ my: 0.75 }} />
      <SectionTitle>Qualificação da Fonte</SectionTitle>

      <MetricGrid>
        <InfoItem
          card
          icon={TierIcon}
          label="Tier"
          value={effectiveTier === 1 ? '1 (Alto)' : effectiveTier === 2 ? '2 (Médio)' : effectiveTier === 3 ? '3 (Baixo)' : '-'}
          color={effectiveTier === 1 ? '#4caf50' : effectiveTier === 2 ? '#ff9800' : '#9e9e9e'}
          tooltip={effectiveTier ? (() => {
            const src = article.tierSource;
            const cat = article.sourceCategory || article.sourceCategories?.[0];
            const aud = article.audience || article.subscribersNumber || 0;

            if (src === 'override') {
              return (
                <TooltipContent
                  eyebrow="Tier · Ajuste Manual"
                  title={`Tier ${effectiveTier} definido pelo usuário`}
                  description="Este Tier foi fixado manualmente no Ajuste Fino de Fontes e tem prioridade máxima sobre qualquer cálculo automático."
                />
              );
            }
            if (src === 'category') {
              return (
                <TooltipContent
                  eyebrow="Tier · Segmento Estratégico"
                  title={`Tier ${effectiveTier} por nicho${cat ? ` (${cat})` : ''}`}
                  description={`A categoria "${cat || 'desta fonte'}" é estratégica para ${activeBrandName}. Fontes especializadas no nicho da marca recebem Tier 1 automaticamente.`}
                />
              );
            }
            if (src === 'size' || src === 'default') {
              return (
                <TooltipContent
                  eyebrow="Tier · Audiência / Alcance"
                  title={`Tier ${effectiveTier} por porte da fonte`}
                  description={`Calculado com base na audiência da fonte (${formatNumber(aud)} ${article.mediaType === 'Online' ? 'visitantes/dia' : 'seguidores'}) em relação ao perfil de porte da marca (${activeBrandData?.tierProfile || 'médio'}).`}
                />
              );
            }
            return null;
          })() : undefined}
        />
        <InfoItem card icon={MapIcon} label="Abrangência" value={sourceReach} color="#14b8a6" />
      </MetricGrid>

          {hasSourceData && (
            <MetricGrid>
              <InfoItem card icon={BusinessIcon} label="Tipo" value={sourceType} />
              <InfoItem card icon={CategoryIcon} label="Segmento" value={sourceSegment} />
              <InfoItem card icon={LocationIcon} label="Localização" value={sourceLocation} />
            </MetricGrid>
          )}
        </>
      )}

      {/* -- Seção: Mensuração Editorial ----------------------------------- */}
      {(!section || section === 'mensuracao') && (
        <>
          <Divider sx={{ my: 0.75 }} />
          <SectionTitle>Mensuração Editorial</SectionTitle>

      <MetricGrid>
        <InfoItem
          card
          icon={isTimeMedia ? ScheduleIcon : StraightenIcon}
          label={isTimeMedia ? 'Minutagem' : (activeEntities.length > 1 ? "Área Editorial Total" : "Área Editorial")}
          value={
            hasEditorialMeasurement
              ? isTimeMedia
                ? `${formatDuration(brandDurationSeconds)} (${Math.round(attributedOccupancyPercent * 100)}%)`
                : activeEntities.length > 1
                ? `${(totalEditorialCm2 || entityMeasuredAreaCm2 || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} cm²`
                : `${attributedOccupancyCm2.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} cm² (${Math.round(attributedOccupancyPercent * 100)}%)`
              : 'Pendente'
          }
          color={hasEditorialMeasurement ? MIA_ACCENT : infoMuted}
          tooltip={
            <TooltipContent
              eyebrow={isTimeMedia ? 'Minutagem' : (activeEntities.length > 1 ? "Área Editorial Total" : "Área Editorial")}
              title={
                isTimeMedia
                  ? `${formatDuration(brandDurationSeconds)} de ${formatDuration(totalDurationSeconds)} (${Math.round(attributedOccupancyPercent * 100)}%)`
                  : activeEntities.length > 1
                  ? `${(totalEditorialCm2 || entityMeasuredAreaCm2 || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} cm²`
                  : `${attributedOccupancyCm2.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} cm² (${Math.round(attributedOccupancyPercent * 100)}%)`
              }
              description={
                isTimeMedia
                  ? `Tempo de exposição atribuído a ${dominantEntityName} dentro da duração total do conteúdo.`
                  : activeEntities.length > 1
                  ? `Esta publicação envolve ${activeEntities.length} marcas com espaços que variam. Consulte a "Análise Qualitativa" para ver a área individual de cada marca.`
                  : `Espaço editorial atribuído a ${dominantEntityName}. Em publicações dedicadas, o contexto da matéria é atribuído à marca principal.`
              }
              evidence={isTimeMedia
                ? `Duração total: ${formatDuration(totalDurationSeconds)} | Minutagem da marca: ${formatDuration(brandDurationSeconds)}`
                : `Área total analisada: ${(totalEditorialCm2 || entityMeasuredAreaCm2 || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} cm² | Espaço atribuído: ${Math.round(attributedOccupancyPercent * 100)}%`}
            />
          }
        />

        {sentimentConfig && (
          <InfoItem
            card
            icon={SentimentIconComponent}
            icons={sentimentIcons}
            label="Sentimento"
            value={sentimentConfig.label}
            color={sentimentConfig.color}
            tooltip={
              <TooltipContent
                eyebrow="Sentimento"
                title={sentimentConfig.label}
                description={
                  activeEntities.length > 1
                    ? `Esta publicação envolve ${activeEntities.length} marcas com sentimentos que podem variar. Consulte a "Análise Qualitativa" para ver o sentimento individual de cada marca.`
                    : activeEntities.length === 1
                    ? `Sentimento de ${dominantEntityName} nesta publicação.`
                    : `Sentimento geral identificado para esta publicação.`
                }
                evidence={evidenceText}
              />
            }
          />
        )}

        {article.exposure && exposureConfig && ExposIcon && (
          <InfoItem
            card
            icon={ExposIcon}
            icons={exposureIcons}
            label="Exposição"
            value={exposureConfig.label}
            color={exposureConfig.color}
            tooltip={
              <TooltipContent
                eyebrow="Exposição"
                title={exposureConfig.label}
                description={
                  activeEntities.length > 0
                    ? `${dominantEntityName} é a marca com maior presença editorial nesta publicação.${ activeEntities.length > 1 ? ' Clique em "Ver Análise Qualitativa" para ver o detalhamento de todas as marcas identificadas.' : '' }`
                    : `Tipo de exposição identificado para esta publicação.`
                }
                evidence={evidenceText}
              />
            }
          />
        )}

        {/* Highlight - Marcas com citação no título */}
        {(() => {
          const titleHighlights = occupancy?.titleHighlights;
          if (!titleHighlights || titleHighlights.length === 0) return null;
          const highlightLabel = titleHighlights.length === 1
            ? titleHighlights[0]
            : `${titleHighlights.length} marcas`;
          const tooltipNames = titleHighlights.join(', ');
          return (
            <InfoItem
              card
              icon={HighlightTitleIcon}
              label="Highlight"
              value={highlightLabel}
              color="#f59e0b"
              tooltip={
                <TooltipContent
                  eyebrow="Highlight no Título"
                  title={highlightLabel}
                  description={`${tooltipNames} ${titleHighlights.length === 1 ? 'aparece' : 'aparecem'} no título desta publicação, indicando protagonismo editorial direto.`}
                />
              }
            />
          );
        })()}

        {/* Porta-voz identificado pelo motor nativo */}
        {spokespersonFound && (
          <InfoItem
            card
            icon={PersonIcon}
            label="Porta-voz"
            value={spokespersonFound}
            color="#3b82f6"
            tooltip={
              <TooltipContent
                eyebrow="Representante Oficial"
                title={spokespersonFound}
                description="Este porta-voz oficial foi identificado no texto através do motor de busca nativo do Mensure."
              />
            }
          />
        )}

        {keyMessagesFound.length > 0 && (
          <InfoItem
            card
            icon={KeyMessageIcon}
            label="Mensagem-chave"
            value="Sim"
            color="#8b5cf6"
            tooltip={
              <TooltipContent
                eyebrow="Mensagem-chave"
                title={keyMessagesFound.length === 1 ? 'Mensagem-chave identificada' : `${keyMessagesFound.length} mensagens-chave identificadas`}
                description="O conteúdo menciona mensagem estratégica cadastrada no briefing."
                evidence={keyMessagesFound.join(' • ')}
              />
            }
          />
        )}
      </MetricGrid>
        </>
      )}

    </Box>
  );
};

TooltipContent.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  insight: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  evidence: PropTypes.string,
  inverted: PropTypes.bool
};

EntityBreakdownChart.propTypes = {
  entities: PropTypes.array,
  inverted: PropTypes.bool
};

ModalArticleInfoBrandAware.propTypes = {
  article: PropTypes.object.isRequired,
  sx: PropTypes.object,
  enableBriefingKeywords: PropTypes.bool,
  section: PropTypes.oneOf(['fonte', 'mensuracao'])
};

export default ModalArticleInfoBrandAware;
